<template>
	<div class="branch-consolidate-mapping">
		<div class="branch-header display-flex">
			<div class="branch-name-container flex-1">
				<div class="branch-name-edit display-flex">
					<input type="text" v-model="branchName" ref="branchName" class="branch-name-input" :size="branchName.length + 4" />
					<v-btn v-if="hasEditPermissions" icon small @click="$refs.branchName.focus()">
						<div class="edit-icon"><EditIcon /></div>
					</v-btn>
				</div>
			</div>
			<div v-if="hasEditPermissions" class="output-toggle col-2 pa-0 ma-0">
				<AppTooltip text="Map a table parameter">
					<v-switch v-model="isTableOutput" hide-details dense>
						<template #label><TableSvg /></template>
					</v-switch>
				</AppTooltip>
			</div>
			<template v-if="hasEditPermissions">
				<AppLoader v-if="isLoadingDelete" class="ml-4 mt-1" />
				<div v-else class="delete-button col-1 pa-0 ma-0">
					<v-btn icon x-small @click="deleteMapping"><v-icon>mdi-delete</v-icon></v-btn>
				</div>
			</template>
		</div>
		<div class="branch-param-body">
			<div class="param-container">
				<div class="param" v-for="edge in branchRootPaths" :key="edge.id">
					<ParameterBranchMapping
						:item="edge"
						:value="getEdgeValue(edge.id)"
						@updateValue="(value) => onUpdateValue({ branchId: edge.id, value })"
						:valuesTypes="dataInputMenuValueTypes"
						:isSingleSelection="isTableOutput"
						:hasEditPermissions="hasEditPermissions"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import debounce from 'lodash/debounce';
import EditIcon from './assets/edit.svg';
import { mapGetters } from 'vuex';
import ParameterBranchMapping from './ParameterBranchMapping';

import {
	ITEM_ONLY_ALLOWED_VALUE_TYPES,
	TABLE_ONLY_ALLOWED_VALUE_TYPES,
} from '@/components/DataInput/DataInputMenu/helpers/data-input-menu.const';
import AppTooltip from '@/components/common/AppTooltip';
import TableSvg from '../BlockEditorParams/inputsComponents/assets/TableSvg';

export default {
	name: 'BranchConsolidateMapping',
	props: {
		hasEditPermissions: { type: Boolean },
		isLoadingDelete: { type: Boolean, default: false },
		variable: { type: Object, required: true },
		branchRootPaths: { type: Array, required: true },
	},
	components: {
		ParameterBranchMapping,
		EditIcon,
		AppTooltip,
		TableSvg,
	},
	methods: {
		deleteMapping() {
			this.$emit('deleteMapping', { id: this.mappingId });
		},
		onUpdateValue: debounce(function ({ branchId, value }) {
			const currentValue = this.getEdgeValue(branchId);
			if (currentValue !== value) {
				this.$emit('update-mapping-value', { branchId, value, path: this.mappingId });
			}
		}, 1000),
		getEdgeValue(edgeId) {
			return this.variable.mappings.find(({ path }) => path === edgeId).value;
		},
	},
	computed: {
		...mapGetters('selectedNode', ['editNode']),
		...mapGetters(['nodes/getNodeByUuidForWorkflowVersion']),
		mappingId() {
			return this.variable.uuid;
		},
		allRelevantIncomingEdges() {
			return this.useNodeIncomingEdges.filter(({ type }) => type !== this.branchRootId);
		},
		dataInputMenuValueTypes() {
			return this.isTableOutput ? TABLE_ONLY_ALLOWED_VALUE_TYPES : ITEM_ONLY_ALLOWED_VALUE_TYPES;
		},
		isTableOutput: {
			get() {
				return this.variable.is_table_output;
			},
			set(value) {
				this.$emit('update-path-output-is-table', { id: this.mappingId, isTableOutput: value });
			},
		},
	},
	watch: {
		branchName: {
			handler: debounce(function (newV) {
				this.$emit('update-path-name', { id: this.mappingId, name: newV });
			}, 400),
		},
	},
	data() {
		return {
			branchName: this.variable.name,
		};
	},
};
</script>

<style lang="scss" scoped>
.branch-consolidate-mapping {
	margin: 0 0 10px;
	background-color: #fcfcfd;
	border: 1px solid #d1d9e9;
	border-radius: 9px;
	font-size: 12px;

	.branch-header {
		padding: 10px 15px;
		background-color: #f1f5f8;
		font-size: 14px;
		border-radius: 9px 9px 0 0;

		.delete-button {
			text-align: end;
		}
		.output-toggle {
			::v-deep {
				.v-input--selection-controls {
					margin-top: 0px;
					padding-top: 0px;
				}
			}
		}
		.edit-icon {
			opacity: 0;
			transition: 0.5s;
			transform: scale(0.5);
		}

		&:hover {
			.edit-icon {
				opacity: 1;
				transition: 0.2s;
				transform: scale(0.8);
			}
		}

		input.branch-name-input {
			outline: none;
		}
	}

	.branch-param-body {
		padding: 10px 15px;
	}

	.param-container {
		.param {
			&:not(:first-child) {
				margin: 10px 0 0 0;
			}
			&:last-child ::v-deep .insert-data-container {
				margin: 0 0 10px 0;
			}
		}
	}
}
</style>
